import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';

import { useJavascriptResolver } from 'dashboard/components/GridLayoutRender/computed-properties/useJavascriptResolver';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { AccountAction } from 'dashboard/models/account-action';
import { TriggerAccount } from 'dashboard/models/trigger';
import FormAccountAction from 'dashboard/views/DashboardAccountsShowView/components/FormAccountAction';
import { useFormSecurely } from 'lib/security/reactHookForm';
import { Json } from 'lib/types';
import isValidJSON from 'lib/utils/formValidation/isValidJSON';

export interface AccountActionModalProps {
  accountDataContext: Record<string, unknown>;
  accountAction: AccountAction;
  trigger: TriggerAccount;
  handleRunAccountAction: (values: Json, workflowId: string) => Promise<void>;
  hideModal: () => void;
}

const AccountActionModal = ({
  accountDataContext,
  accountAction,
  trigger,
  handleRunAccountAction,
  hideModal,
}: AccountActionModalProps) => {
  const handleRunAccountActionAndClose = useCallback(
    async (values: Record<string, unknown>) => {
      await handleRunAccountAction(values as Json, accountAction.id);
      hideModal();
    },
    [handleRunAccountAction, hideModal, accountAction],
  );

  // Collect all prefilled values and resolve using account data context
  // e.g { 'param_1': "{{ current_account.fields.age }}" } => { 'param_1': 19 }
  const prefilledValues = trigger.attributes['input-modal-configuration'].reduce(
    (acc: Record<string, string>, schema) => {
      if (!schema.prefill) {
        return acc;
      } else {
        acc[schema.key] = schema.prefill;
        return acc;
      }
    },
    {},
  );

  const [isLoadingPrefilled, resolvedPrefillValues, prefillError] = useJavascriptResolver(
    JSON.stringify(prefilledValues),
    accountDataContext,
    '{}',
  );

  const defaultValues = useMemo(() => {
    return !prefillError && resolvedPrefillValues && isValidJSON(resolvedPrefillValues)
      ? JSON.parse(resolvedPrefillValues)
      : {};
  }, [JSON.stringify(resolvedPrefillValues), prefillError]);

  const accountActionFormContext = useFormSecurely({
    mode: 'onChange',
    defaultValues: defaultValues,
  });

  // Reset the form when the prefilled values get reset (e.g. when the account action is updated)
  // so that prefilled values are always visible in the form.
  useEffect(() => {
    accountActionFormContext.reset(defaultValues);
  }, [JSON.stringify(defaultValues)]);

  if (isLoadingPrefilled) {
    return <LoadingIndicator fullscreen />;
  } else {
    return (
      <ModalContainer>
        <ModalHeader onClose={hideModal}>{accountAction.attributes.name}</ModalHeader>
        <ModalBody>
          <FormProvider {...accountActionFormContext}>
            <FormAccountAction
              trigger={trigger}
              accountDataContext={accountDataContext}
              handleRunAction={handleRunAccountActionAndClose}
              handleCancel={hideModal}
            />
          </FormProvider>
        </ModalBody>
      </ModalContainer>
    );
  }
};

export default AccountActionModal;
